import { useState, useEffect, useRef, useCallback } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { LuAudioLines, LuLoaderCircle } from 'react-icons/lu'
import { FiCopy, FiTrash, FiImage, FiDownload, FiZoomIn, FiZoomOut, FiMaximize2, FiX, FiCamera, FiScissors, FiChevronLeft, FiChevronRight, FiEdit2 } from 'react-icons/fi'
import { MdDriveFileMove } from 'react-icons/md'

import VideoPlayer from './VideoPlayer'
import Button from './ui/Button'
import AsyncEditableTextArea from './ui/AsyncEditableTextArea'
import { Progress } from './ui/shadcn/progress'
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/shadcn/tabs'
import { useShow } from '@/contexts/ShowContext'
import { UserRole } from '@/contexts/UserContext'
import { useModal } from '@/contexts/ModalContext'
import { apiFetch } from '@/lib/api'
import { isTrimSupported } from '@/lib/trimVideo'
import useFileUpload from '@/hooks/useFileUpload'
import useFileDownload from '@/hooks/useFileDownload'
import CommentGroup from './comments/CommentGroup'
import CommentMentionsInput from './comments/CommentMentionsInput'
import FolderPickerModal from './modals/FolderPickerModal'
import TaskModal from './modals/TaskModal'
import TagsModal from './modals/TagsModal'

// Format byte size into a human-readable string. Returns null if size is missing.
const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return null
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let i = 0
    let n = bytes
    while (n >= 1024 && i < units.length - 1) {
        n /= 1024
        i++
    }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`
}

const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds) || seconds <= 0) return null
    const s = Math.round(seconds)
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return h > 0
        ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
        : `${m}:${String(sec).padStart(2, '0')}`
}

const MIN_ZOOM = 1
const MAX_ZOOM = 8
const ZOOM_STEP = 0.25

function ImagePreview({ asset }) {
    const containerRef = useRef(null)
    const [scale, setScale] = useState(1)
    const [offset, setOffset] = useState({ x: 0, y: 0 })
    const dragState = useRef(null)
    // Keep refs to the latest scale / pinch baseline so the non-passive wheel
    // and gesture listeners (attached via addEventListener in useEffect) can
    // read current state without being re-bound on every render.
    const scaleRef = useRef(scale)
    useEffect(() => { scaleRef.current = scale }, [scale])
    const gestureStartScaleRef = useRef(1)

    const clampScale = (s) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, s))

    const reset = useCallback(() => {
        setScale(1)
        setOffset({ x: 0, y: 0 })
    }, [])

    // Reset zoom when asset changes
    useEffect(() => {
        reset()
    }, [asset?._id, asset?.url, reset])

    // Zoom around a focal point (clientX/clientY in viewport coords). Keeps the
    // pixel under the cursor anchored as the scale changes.
    const zoomAt = useCallback((nextScale, focalClientX, focalClientY) => {
        const target = clampScale(nextScale)
        setScale((prevScale) => {
            const clamped = clampScale(target)
            if (clamped === prevScale) return prevScale
            const container = containerRef.current
            if (!container) return clamped
            const rect = container.getBoundingClientRect()
            const cx = focalClientX != null ? focalClientX - rect.left - rect.width / 2 : 0
            const cy = focalClientY != null ? focalClientY - rect.top - rect.height / 2 : 0
            setOffset((prevOffset) => {
                const ratio = clamped / prevScale
                const nx = cx - (cx - prevOffset.x) * ratio
                const ny = cy - (cy - prevOffset.y) * ratio
                if (clamped <= 1) return { x: 0, y: 0 }
                return { x: nx, y: ny }
            })
            return clamped
        })
    }, [])

    // Wheel + Safari gesture listeners. We attach these manually (instead of
    // using React's onWheel) so we can opt out of the default passive mode
    // and call preventDefault — otherwise the browser still applies its
    // page-zoom gesture on top of ours.
    useEffect(() => {
        const el = containerRef.current
        if (!el) return

        const onWheel = (e) => {
            const isZoomGesture = e.ctrlKey || e.metaKey
            if (isZoomGesture) {
                // Trackpad pinch (ctrlKey set by the browser) and Cmd/Ctrl+wheel both zoom.
                e.preventDefault()
                const factor = Math.exp(-e.deltaY * 0.01)
                zoomAt(scaleRef.current * factor, e.clientX, e.clientY)
                return
            }
            if (scaleRef.current > 1) {
                e.preventDefault()
                setOffset((prev) => ({
                    x: prev.x - e.deltaX,
                    y: prev.y - e.deltaY,
                }))
            }
        }

        // Safari fires non-standard gesture* events for trackpad pinch. They
        // bypass the wheel path entirely and trigger page zoom unless we
        // preventDefault them. e.scale is multiplicative relative to gesturestart.
        const onGestureStart = (e) => {
            e.preventDefault()
            gestureStartScaleRef.current = scaleRef.current
        }
        const onGestureChange = (e) => {
            e.preventDefault()
            const next = gestureStartScaleRef.current * (e.scale || 1)
            zoomAt(next, e.clientX, e.clientY)
        }
        const onGestureEnd = (e) => {
            e.preventDefault()
        }

        el.addEventListener('wheel', onWheel, { passive: false })
        el.addEventListener('gesturestart', onGestureStart, { passive: false })
        el.addEventListener('gesturechange', onGestureChange, { passive: false })
        el.addEventListener('gestureend', onGestureEnd, { passive: false })
        return () => {
            el.removeEventListener('wheel', onWheel)
            el.removeEventListener('gesturestart', onGestureStart)
            el.removeEventListener('gesturechange', onGestureChange)
            el.removeEventListener('gestureend', onGestureEnd)
        }
    }, [zoomAt])

    const handleMouseDown = (e) => {
        if (scale <= 1) return
        e.preventDefault()
        dragState.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: offset.x,
            originY: offset.y,
        }
    }

    const handleMouseMove = (e) => {
        if (!dragState.current) return
        const { startX, startY, originX, originY } = dragState.current
        setOffset({
            x: originX + (e.clientX - startX),
            y: originY + (e.clientY - startY),
        })
    }

    const endDrag = () => {
        dragState.current = null
    }

    return (
        <div
            ref={containerRef}
            className='relative w-full h-full bg-black overflow-hidden select-none touch-none overscroll-contain'
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            style={{ cursor: scale > 1 ? (dragState.current ? 'grabbing' : 'grab') : 'default' }}
        >
            <div
                className='absolute inset-0 flex items-center justify-center'
                style={{
                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                    transformOrigin: 'center center',
                    transition: dragState.current ? 'none' : 'transform 120ms ease-out',
                }}
            >
                <img
                    src={asset.url || asset.thumbnail_url}
                    alt={asset.name}
                    decoding='async'
                    draggable={false}
                    className='object-contain w-full h-full'
                />
            </div>

            <div className='absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-md bg-black/60 backdrop-blur px-1 py-1 text-white text-xs'>
                <button
                    type='button'
                    title='Zoom out'
                    disabled={scale <= MIN_ZOOM}
                    onClick={() => zoomAt(scale - ZOOM_STEP)}
                    className='p-1 rounded hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent'
                >
                    <FiZoomOut className='w-4 h-4' />
                </button>
                <button
                    type='button'
                    title='Reset zoom'
                    onClick={reset}
                    className='px-2 py-1 rounded hover:bg-white/10 min-w-[3.25rem] text-center tabular-nums'
                >
                    {Math.round(scale * 100)}%
                </button>
                <button
                    type='button'
                    title='Zoom in'
                    disabled={scale >= MAX_ZOOM}
                    onClick={() => zoomAt(scale + ZOOM_STEP)}
                    className='p-1 rounded hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent'
                >
                    <FiZoomIn className='w-4 h-4' />
                </button>
                <div className='w-px h-4 bg-white/20 mx-1' />
                <button
                    type='button'
                    title='Fit to screen'
                    onClick={reset}
                    className='p-1 rounded hover:bg-white/10'
                >
                    <FiMaximize2 className='w-4 h-4' />
                </button>
            </div>
        </div>
    )
}

function AssetMedia({ asset, videoPlayerRef }) {
    if (!asset) return null
    if (asset.mediaType === 'video') {
        return (
            <div className='w-full h-full flex items-center justify-center bg-black'>
                <VideoPlayer ref={videoPlayerRef} asset={asset} autoPlay allowTrim className='w-full h-full' />
            </div>
        )
    }
    if (asset.mediaType === 'audio') {
        return (
            <div className='w-full h-full flex flex-col items-center justify-center gap-6 bg-black p-6'>
                <LuAudioLines className='w-24 h-24 text-secondary-foreground/60' />
                <audio src={asset.url} autoPlay controls className='w-full max-w-[480px]' />
            </div>
        )
    }
    if (asset.mediaType === 'image') {
        return <ImagePreview asset={asset} />
    }
    return (
        <div className='w-full h-full flex items-center justify-center bg-black text-secondary-foreground/60 text-sm'>
            No preview available
        </div>
    )
}

function InfoRow({ label, children }) {
    return (
        <div className='flex flex-col gap-1'>
            <span className='text-[11px] uppercase tracking-wide text-secondary-foreground/60'>{label}</span>
            <div className='text-sm text-foreground break-words'>{children}</div>
        </div>
    )
}

// Settings keys that are media references (inputs/outputs) rather than tunable
// generation parameters — hidden from the settings chip list.
const GENERATION_HIDDEN_KEYS = ['prompt', 'asset', 'assets', 'video', 'audio', 'image', 'image_url']

// Shows the generation that produced an asset (model, prompt, settings). The
// asset already carries a `generation` sub-document with the payload settings;
// we additionally fetch the underlying task async to resolve the model name and
// enable opening full task details. The section renders immediately from the
// embedded generation data and enriches once the task loads.
function GenerationInfo({ asset }) {
    const gen = asset?.generation
    const taskId = gen?.task
    const { openModal } = useModal()
    const [task, setTask] = useState(null)

    useEffect(() => {
        let cancelled = false
        setTask(null)
        if (!taskId) return
        const fetchTask = async () => {
            try {
                const res = await apiFetch(`/api/tasks/${taskId}`)
                if (!res.ok) throw new Error('Failed to fetch task')
                const data = await res.json()
                if (!cancelled) setTask(data)
            } catch (error) {
                console.error('Error fetching generation task:', error)
            }
        }
        fetchTask()
        return () => { cancelled = true }
    }, [taskId])

    if (!gen) return null

    // The task's request body is the source of truth; fall back to the settings
    // snapshot stored on the asset when the task can't be loaded.
    const payload = task?.request_body?.payload || gen.settings || {}
    const prompt = payload.prompt || gen.prompt
    const modelName = task?.model?.name || (typeof gen.model === 'object' ? gen.model?.name : null)
    const settingsEntries = Object.entries(payload).filter(([key, value]) =>
        !GENERATION_HIDDEN_KEYS.includes(key) &&
        value !== null &&
        value !== undefined &&
        value !== '' &&
        typeof value !== 'object',
    )

    return (
        <div className='flex flex-col gap-4'>
            {/* <div className='flex items-center justify-between'>
                <span className='text-[11px] uppercase tracking-wide text-secondary-foreground/60'>Generation</span>
                {task && (
                    <button
                        type='button'
                        onClick={() => openModal(<TaskModal task={task} />, 'side')}
                        className='text-[11px] text-primary hover:underline cursor-pointer'
                    >
                        View task
                    </button>
                )}
            </div> */}

            {modelName && (
                <InfoRow label='Model'>
                    <span className='text-xs bg-secondary/60 rounded-sm px-1.5 py-0.5 text-secondary-foreground'>{modelName}</span>
                </InfoRow>
            )}

            {prompt && (
                <InfoRow label='Prompt'>
                    <span className='block text-xs font-mono text-secondary-foreground bg-secondary/40 rounded-md p-2 whitespace-pre-wrap'>{prompt}</span>
                </InfoRow>
            )}

            {settingsEntries.length > 0 && (
                <InfoRow label='Settings'>
                    <div className='flex flex-wrap gap-1.5 mt-1'>
                        {settingsEntries.map(([key, value]) => (
                            <div key={key} className='flex items-center gap-1.5 bg-secondary/50 rounded-md px-2 py-1'>
                                <span className='text-secondary-foreground/60 text-[10px] capitalize'>{key.replace(/_/g, ' ')}</span>
                                <span className='text-secondary-foreground text-xs'>
                                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                                </span>
                            </div>
                        ))}
                    </div>
                </InfoRow>
            )}
        </div>
    )
}

function InfoTab({ asset, onRefreshAsset }) {
    const { show } = useShow()
    const { openModal } = useModal()

    if (!asset) {
        return null
    }

    const tagNames = (asset.tags || [])
        .map(tagId => show?.asset_categories?.find(t => t._id === tagId)?.name)
        .filter(Boolean)

    // Edit tags in the shared side-tray modal. The refresh is passed in as
    // `onSaved` (owned by an ancestor, not gated on the modal closing) so it
    // still runs if the tray — or the whole preview — is closed before the save
    // completes.
    const openTagsModal = () => {
        openModal(
            <TagsModal
                assetCategories={show?.asset_categories || []}
                assets={[asset]}
                onSaved={() => onRefreshAsset?.(asset._id)}
            />
        )
    }

    const sizeLabel = formatBytes(asset.size ?? asset.metadata?.size)
    const durationLabel = formatDuration(asset.duration)
    const created = asset.created_at || asset.createdAt
    const updated = asset.updatedAt

    const dimensions = (() => {
        const w = asset.metadata?.width || asset.width
        const h = asset.metadata?.height || asset.height
        if (w && h) return `${w} × ${h}`
        return null
    })()

    return (
        <div className='flex flex-col gap-4 p-4 overflow-y-auto h-full'>
            {asset.description && <InfoRow label='Description'>{asset.description}</InfoRow>}
            {asset.aiDescription && (
                <InfoRow label='AI Description'>
                    <span className='text-secondary-foreground/80'>{asset.aiDescription}</span>
                </InfoRow>
            )}
            <div className='grid grid-cols-2 gap-4'>
                <InfoRow label='Type'>{asset.mediaType || '-'}</InfoRow>
                <InfoRow label='Status'>
                    <span className='capitalize'>{asset.status || 'draft'}</span>
                </InfoRow>
                {dimensions && <InfoRow label='Dimensions'>{dimensions}</InfoRow>}
                {durationLabel && <InfoRow label='Duration'>{durationLabel}</InfoRow>}
                {sizeLabel && <InfoRow label='Size'>{sizeLabel}</InfoRow>}
                {asset.source && <InfoRow label='Source'>{asset.source}</InfoRow>}
                {created && (
                    <InfoRow label='Created'>{dayjs(created).format('MMM D, YYYY h:mm A')}</InfoRow>
                )}
            </div>
            <div className='flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                    <span className='text-[11px] uppercase tracking-wide text-secondary-foreground/60'>Tags</span>
                    <button
                        type='button'
                        onClick={openTagsModal}
                        title='Edit tags'
                        className='p-0.5 rounded-sm text-secondary-foreground/70 hover:text-secondary-foreground cursor-pointer'
                    >
                        <FiEdit2 className='w-3.5 h-3.5' />
                    </button>
                </div>
                <div className='text-sm text-foreground break-words'>
                    {tagNames.length > 0 ? (
                        <div className='flex flex-wrap gap-1 mt-1'>
                            {tagNames.map((name) => (
                                <span key={name} className='text-[11px] py-[1px] px-1.5 rounded-sm capitalize bg-primary text-primary-foreground'>
                                    {name}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <span className='text-xs text-secondary-foreground/60'>No tags</span>
                    )}
                </div>
            </div>

            <GenerationInfo asset={asset} />
            
            {asset.url && (
                <InfoRow label='URL'>
                    <a
                        href={asset.url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary hover:underline text-xs break-all'
                    >
                        {asset.url}
                    </a>
                </InfoRow>
            )}
            
        </div>
    )
}

// Asset comments tab — mirrors the TimelineComments pattern (CommentGroup +
// CommentMentionsInput + optimistic add/update) but without the timeline-only
// concerns (playhead timestamp, episode/segment context).
function AssetComments({ assetId }) {
    const { show } = useShow()
    const [comments, setComments] = useState([])
    const [loading, setLoading] = useState(true)
    const [commentText, setCommentText] = useState('')
    const [posting, setPosting] = useState(false)
    const commentInputRef = useRef(null)

    const fetchComments = useCallback(async () => {
        if (!show?._id || !assetId) {
            setComments([])
            setLoading(false)
            return
        }
        try {
            const res = await apiFetch(`/api/shows/${show._id}/comments?uid=${assetId}`)
            if (!res.ok) throw new Error('Failed to fetch comments')
            const data = await res.json()
            setComments(data)
        } catch (error) {
            console.error('Error fetching comments:', error)
        } finally {
            setLoading(false)
        }
    }, [show?._id, assetId])

    useEffect(() => {
        setLoading(true)
        fetchComments()
    }, [fetchComments])

    const addComment = useCallback((comment) => {
        setComments(prev => [...prev, comment])
    }, [])

    const updateComment = useCallback((updated) => {
        setComments(prev => prev.map(c => c._id === updated._id ? { ...c, ...updated } : c))
    }, [])

    const handleCommentSubmit = async () => {
        if (!commentText.trim() || posting) return
        try {
            setPosting(true)
            const res = await apiFetch(`/api/shows/${show._id}/comments`, {
                method: 'POST',
                body: JSON.stringify({
                    uid: assetId,
                    text: commentText,
                    element_type: 'asset',
                }),
            })
            if (!res.ok) throw new Error('Failed to submit comment')
            const data = await res.json()
            addComment(data)
            setCommentText('')
        } catch (error) {
            console.error('Error submitting comment:', error)
        } finally {
            setPosting(false)
        }
    }

    const handleCommentKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault()
            handleCommentSubmit()
        } else if (e.key === 'Escape') {
            setCommentText('')
        }
    }

    return (
        <div className='flex flex-col h-full bg-background/50'>
            <div className='flex-1 overflow-y-auto px-2 py-2'>
                {loading ? (
                    <div className='flex items-center justify-center py-8'>
                        <LuLoaderCircle className='w-4 h-4 animate-spin text-secondary-foreground/50' />
                    </div>
                ) : comments.length === 0 ? (
                    <div className='flex items-center justify-center py-8'>
                        <span className='text-xs text-secondary-foreground/40'>No comments yet</span>
                    </div>
                ) : (
                    <div className='flex flex-col gap-2'>
                        {comments.map((comment) => (
                            <CommentGroup
                                key={comment._id}
                                data={comment}
                                onDelete={fetchComments}
                                onUpdate={updateComment}
                            />
                        ))}
                    </div>
                )}
            </div>

            <UserRole role='commentator'>
                <div className='p-2 border-t'>
                    <div className='flex flex-col gap-1'>
                        <CommentMentionsInput
                            value={commentText}
                            onChange={setCommentText}
                            onKeyDown={handleCommentKeyDown}
                            placeholder='Add a comment...'
                            inputRef={commentInputRef}
                        />
                        <div className='flex items-center justify-end gap-1'>
                            <Button type='confirm' size='xs' onClick={handleCommentSubmit} disabled={!commentText.trim() || posting}>
                                Comment
                            </Button>
                        </div>
                    </div>
                </div>
            </UserRole>
        </div>
    )
}

// Renders the actions section below the tabs. Each action is rendered only when
// its callback is provided so callers can opt-in to specific actions. The modal
// holds no logic itself — every button just invokes the parent callback with the
// asset.
function ActionsSection({ asset, onDelete, onCopy, onMove, onSketchEdit, onCaptureFrame, capturing, onTrim, trimming, trimProgress, onDownload }) {
    const actions = []

    if (onCaptureFrame && asset.mediaType === 'video') {
        actions.push(
            <Button
                key='capture'
                type='secondary'
                disabled={capturing}
                icon={capturing
                    ? <LuLoaderCircle className='w-4 h-4 animate-spin' />
                    : <FiCamera className='w-4 h-4' />}
                onClick={() => onCaptureFrame(asset)}
            >
                {capturing ? 'Capturing…' : 'Capture Frame'}
            </Button>,
        )
    }

    if (onTrim && asset.mediaType === 'video' && isTrimSupported()) {
        actions.push(
            <Button
                key='trim'
                type='secondary'
                disabled={trimming}
                icon={trimming
                    ? <LuLoaderCircle className='w-4 h-4 animate-spin' />
                    : <FiScissors className='w-4 h-4' />}
                onClick={() => onTrim(asset)}
            >
                {trimming
                    ? (trimProgress > 0 ? `Trimming… ${Math.round(trimProgress * 100)}%` : 'Trimming…')
                    : 'Trim Video'}
            </Button>,
        )
    }

    if (onSketchEdit && asset.mediaType === 'image') {
        actions.push(
            <Button
                key='edit'
                type='secondary'
                icon={<FiImage className='w-4 h-4' />}
                onClick={() => onSketchEdit(asset)}
            >
                Edit
            </Button>,
        )
    }

    if (asset?.url) {
        actions.push(
            <Button
                key='download'
                type='secondary'
                icon={<FiDownload className='w-4 h-4' />}
                onClick={() => onDownload(asset)}
            >
                Download
            </Button>,
        )
    }

    if (onCopy) {
        actions.push(
            <Button
                key='copy'
                type='secondary'
                icon={<FiCopy className='w-4 h-4' />}
                onClick={() => onCopy(asset)}
            >
                Duplicate
            </Button>,
        )
    }

    if (onMove) {
        actions.push(
            <Button
                key='move'
                type='secondary'
                icon={<MdDriveFileMove className='w-4 h-4' />}
                onClick={() => onMove(asset)}
            >
                Move
            </Button>,
        )
    }

    if (onDelete) {
        actions.push(
            <Button
                key='delete'
                type='danger'
                icon={<FiTrash className='w-4 h-4' />}
                onClick={() => onDelete(asset)}
            >
                Delete
            </Button>,
        )
    }

    if (actions.length === 0) return null

    return (
        <div className='border-t p-3'>
            <div className='grid grid-cols-2 gap-2'>
                {actions}
            </div>
        </div>
    )
}

export default function AssetPreview({ asset, onDelete, onCopy, onMove, onSketchEdit, onCaptured, onRenamed, onRefreshAsset, onClose, onPrev, onNext, positionLabel, initialTab = 'info' }) {
    const [tab, setTab] = useState(initialTab)
    const [capturing, setCapturing] = useState(false)
    const [trimming, setTrimming] = useState(false)
    const [trimProgress, setTrimProgress] = useState(0)
    const [trimStatus, setTrimStatus] = useState('')
    // UI-only lifecycle flags for the inline rename spinner/error. The name
    // itself always comes from the `asset` prop — see handleRenameSave.
    const [savingName, setSavingName] = useState(false)
    const [nameError, setNameError] = useState(null)
    const videoPlayerRef = useRef(null)
    const { show } = useShow()
    const { openModal } = useModal()
    const { uploadFile } = useFileUpload()
    const { downloadFile } = useFileDownload()

    const handleDownload = useCallback(async (target) => {
        if (!target?.url) return
        try {
            await downloadFile(target.url, target.name)
        } catch (error) {
            console.error('Error downloading file:', error)
            toast.error('Failed to download asset')
        }
    }, [downloadFile])

    // Clear any stale rename error when the previewed asset changes.
    useEffect(() => {
        setNameError(null)
    }, [asset?._id])

    // Persist an inline name edit. Mirrors AssetItem's rename: PUT the new name,
    // then let the parent re-fetch so the FE re-renders from the authoritative
    // server data (we keep the spinner up across the refetch so the old name
    // never flashes back). No local copy of the name is kept — it always comes
    // from the `asset` prop. Spinner/error surface via savingName/nameError.
    const handleRenameSave = useCallback(async (name) => {
        if (!show?._id || !asset?._id) return
        setSavingName(true)
        setNameError(null)
        try {
            const res = await apiFetch(`/api/shows/${show._id}/assets/${asset._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name }),
            })
            if (!res.ok) throw new Error('Failed to rename asset')
            await onRenamed?.(asset._id)
        } catch (error) {
            console.error('Error renaming asset:', error)
            setNameError('Failed to rename asset')
        } finally {
            setSavingName(false)
        }
    }, [show?._id, asset?._id, onRenamed])

    // Capture the current video frame, upload it, and create a new image asset
    // in the same folder as the source video.
    const handleCaptureFrame = useCallback(async () => {
        if (!show?._id || capturing) return
        const player = videoPlayerRef.current
        if (!player?.captureFrame) return
        try {
            setCapturing(true)
            const blob = await player.captureFrame('image/png')
            const baseName = asset.name || 'Asset'
            const file = new File([blob], `${baseName}-frame.png`, { type: 'image/png' })
            const uploadResponse = await uploadFile(file)
            const res = await apiFetch(`/api/shows/${show._id}/assets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...uploadResponse,
                    mediaType: 'image',
                    name: `Frame capture from ${baseName}`,
                    source: 'capture',
                    folder: asset.folder || null,
                }),
            })
            if (!res.ok) throw new Error('Failed to create asset')
            const created = await res.json()
            toast.success('Frame captured')
            onCaptured?.(created)
        } catch (error) {
            console.error('Error capturing frame:', error)
            toast.error('Failed to capture frame')
        } finally {
            setCapturing(false)
        }
    }, [show?._id, asset, uploadFile, capturing, onCaptured])

    // Trim the current video to its in/out selection, upload the result, and
    // create a new video asset. The user first picks a destination folder and a
    // name via the folder picker (with naming enabled).
    const handleTrim = useCallback(async () => {
        if (!show?._id || trimming) return
        const player = videoPlayerRef.current
        if (!player?.trim || !player?.getTrimRange) return

        const range = player.getTrimRange()
        if (!range || !(range.end > range.start)) {
            toast.error('Set a valid trim range first')
            return
        }

        const baseName = asset.name || 'Asset'
        const pick = await openModal(
            <FolderPickerModal
                title='Trim Video'
                confirmLabel='Trim & Save'
                allowNaming
                nameLabel='New asset name'
                namePlaceholder='Trimmed clip name'
                initialName={`${baseName} (trimmed)`}
            />,
        )
        if (!pick) return

        try {
            setTrimming(true)
            setTrimProgress(0)
            setTrimStatus('Processing video…')
            const blob = await player.trim(range.start, range.end, {
                onProgress: setTrimProgress,
            })
            setTrimStatus('Uploading…')
            const file = new File([blob], `${pick.assetName}.mp4`, { type: 'video/mp4' })
            const uploadResponse = await uploadFile(file)
            setTrimStatus('Creating asset…')
            const res = await apiFetch(`/api/shows/${show._id}/assets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...uploadResponse,
                    mediaType: 'video',
                    name: pick.assetName,
                    source: 'trim',
                    folder: pick.folderId ?? null,
                }),
            })
            if (!res.ok) throw new Error('Failed to create asset')
            const created = await res.json()
            toast.success('Trimmed video created')
            onCaptured?.(created)
        } catch (error) {
            if (error?.name === 'AbortError') return
            console.error('Error trimming video:', error)
            toast.error('Failed to trim video')
        } finally {
            setTrimming(false)
            setTrimProgress(0)
            setTrimStatus('')
        }
    }, [show?._id, asset, uploadFile, trimming, openModal, onCaptured])

    if (!asset) return null

    return (
        <div id={`${AssetPreview.name}`} className='relative flex w-full h-full overflow-hidden'>
            {/* Full-cover progress overlay shown while a trim is in flight. */}
            {trimming && (
                <div className='absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm'>
                    <div className='flex flex-col items-center gap-4 w-[320px] max-w-[80%] px-6'>
                        <div className='relative flex items-center justify-center'>
                            <LuLoaderCircle className='w-12 h-12 text-primary animate-spin' />
                            <FiScissors className='w-5 h-5 text-primary absolute' />
                        </div>
                        <div className='flex flex-col items-center gap-1 w-full'>
                            <span className='text-sm font-medium text-foreground'>{trimStatus || 'Trimming video…'}</span>
                            <span className='text-xs text-secondary-foreground/60'>{Math.round(trimProgress * 100)}%</span>
                        </div>
                        <Progress value={Math.round(trimProgress * 100)} className='w-full' />
                        <span className='text-[11px] text-secondary-foreground/50 text-center'>
                            Trimming runs in your browser — keep this tab open.
                        </span>
                    </div>
                </div>
            )}
            <div className='relative flex-1 min-w-0 bg-black flex items-center justify-center'>
                <AssetMedia key={asset._id} asset={asset} videoPlayerRef={videoPlayerRef} />

                {onPrev && (
                    <button
                        type='button'
                        title='Previous'
                        onClick={onPrev}
                        className='absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70 cursor-pointer'
                    >
                        <FiChevronLeft className='w-5 h-5' />
                    </button>
                )}

                {onNext && (
                    <button
                        type='button'
                        title='Next'
                        onClick={onNext}
                        className='absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 backdrop-blur text-white hover:bg-black/70 cursor-pointer'
                    >
                        <FiChevronRight className='w-5 h-5' />
                    </button>
                )}

                {positionLabel && (
                    <div className='absolute top-3 left-1/2 -translate-x-1/2 z-10 rounded-md bg-black/60 backdrop-blur px-2 py-1 text-white text-xs tabular-nums pointer-events-none'>
                        {positionLabel}
                    </div>
                )}
            </div>
            <div className='w-[380px] shrink-0 flex flex-col border-l bg-background'>
                <div className='flex items-start gap-2 px-4 pt-4 pb-2'>
                    <div className='min-w-0 flex-1'>
                        <AsyncEditableTextArea
                            value={asset.name}
                            onSave={handleRenameSave}
                            loading={savingName}
                            error={nameError}
                            placeholder='Asset'
                            title='Double-click to rename'
                            variant='heading'
                        />
                        {asset.mediaType && (
                            <span className='text-xs text-secondary-foreground/60 capitalize'>{asset.mediaType}</span>
                        )}
                    </div>
                    {onClose && (
                        <button
                            type='button'
                            onClick={() => onClose(null)}
                            className='shrink-0 -mr-1 -mt-1 p-1.5 rounded-md text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer'
                            title='Close'
                        >
                            <FiX className='w-5 h-5' />
                        </button>
                    )}
                </div>
                <Tabs value={tab} onValueChange={setTab} className='flex-1 min-h-0 flex flex-col gap-0'>
                    <TabsList className='w-full rounded-none'>
                        <TabsTrigger value='info'>Info</TabsTrigger>
                        <TabsTrigger value='comments'>Comments</TabsTrigger>
                    </TabsList>
                    <TabsContent value='info' className='flex-1 min-h-0 overflow-hidden'>
                        <InfoTab asset={asset} onRefreshAsset={onRefreshAsset} />
                    </TabsContent>
                    <TabsContent value='comments' className='flex-1 min-h-0 overflow-hidden'>
                        <AssetComments assetId={asset._id} />
                    </TabsContent>
                </Tabs>
                <ActionsSection
                    asset={asset}
                    onDelete={onDelete}
                    onCopy={onCopy}
                    onMove={onMove}
                    onSketchEdit={onSketchEdit}
                    onCaptureFrame={handleCaptureFrame}
                    capturing={capturing}
                    onTrim={handleTrim}
                    trimming={trimming}
                    trimProgress={trimProgress}
                    onDownload={handleDownload}
                />
            </div>
        </div>
    )
}
